import { Spacing } from '@persona-id/design-tokens';
import { capitalize, compact } from 'lodash/fp';
import { DateTime } from 'luxon';
import { useCallback } from 'react';
import { BORDER_RADIUSES } from 'shared/styles/variables';
import styled, { css } from 'styled-components';

import ContextMenu from 'dashboard/components/ContextMenu';
import ModalWrapper from 'dashboard/components/ModalWrapper';
import Permissioned from 'dashboard/components/Permissioned';
import ReactSelect from 'dashboard/components/ReactSelect';
import ConfirmationModal from 'dashboard/components-lib/ConfirmationModal';
import FormUpdateRoles, { FormUpdateRolesData } from 'dashboard/forms/FormUpdateRoles';
import { ModalBody, ModalContainer, ModalHeader } from 'dashboard/layouts/ModalLayout';
import { Role } from 'dashboard/models/role';
import { Team } from 'dashboard/models/team';
import { User, UserAvailability, UserResponseAttributes, UserStatus } from 'dashboard/models/user';
import { updateAvailability as updateCurrentUserAvailability } from 'dashboard/redux/thunk-actions/current-user';
import { updateUserAvailability } from 'dashboard/redux/thunk-actions/user';
import UserListRowAvailabilityOption from 'dashboard/views/DashboardUsersView/UserListRowAvailabilityOption';
import UserListRowAvailabilitySingleValue from 'dashboard/views/DashboardUsersView/UserListRowAvailabilitySingleValue';
import Sticker from 'design-system/components/Sticker';
import { TableCell, TableRow } from 'design-system/components/Table';
import { Paragraph } from 'design-system/components/Text';
import IconUserProfilePicture from 'images/icons/user-profile-picture.svg';
import { DATE_AND_TIME_TZ_FORMAT } from 'lib/constants/luxonFormats';
import usePermission from 'lib/hooks/usePermission';
import useToggle from 'lib/hooks/useToggle';
import { Permission } from 'lib/permissions';

const PROFILE_PICTURE_WIDTH = 24;

const customStyles = {
  control: () => ({
    borderWidth: 0,
    boxShadow: 'none !important',
  }),
};

interface UsersListRowProps {
  user: User;
  currentUser: User;
  roles: Role[];
  teams: Team[];
  changeUserRoles: (user: User, roleIds: string[]) => Promise<void>;
  removeUser: (user: User) => void;
  reactivateUser: (user: User) => void;
  reinviteUser?: (user: User) => Promise<void>;
  rotateRecoveryCode: (userId: string) => Promise<void>;
}

const UsersListRow = ({
  currentUser,
  user,
  roles,
  teams,
  changeUserRoles,
  reinviteUser,
  removeUser,
  reactivateUser,
  rotateRecoveryCode,
}: UsersListRowProps) => {
  const [
    shouldShowConfirmReinviteModal,
    { enable: showConfirmReinviteModal, disable: hideConfirmReinviteModal },
  ] = useToggle(false);

  const [updateRolesModal, { enable: showUpdateRolesModal, disable: hideUpdateRolesModal }] =
    useToggle(false);

  const isCurrentUser = user.id === currentUser?.id;

  const handleUpdateRoles = useCallback(
    async (values: FormUpdateRolesData) => {
      await changeUserRoles(user, values.roleIds);
      hideUpdateRolesModal();
    },
    [changeUserRoles, user],
  );

  const handleUpdateAvailability = useCallback(
    async (availability: UserAvailability) => {
      if (isCurrentUser) {
        await updateCurrentUserAvailability(availability);
      } else {
        await updateUserAvailability(user.id, availability);
      }
    },
    [user, isCurrentUser],
  );

  const hasUserWritePermission = usePermission(Permission.UserWrite);
  const hasUserAvailabilityWritePermission = usePermission(
    Permission.UserAvailabilityAllUsersWrite,
  );

  const {
    'email-address': emailAddress,
    'available-mfa-methods': availableMfaMethods,
    'invite-expired': inviteExpired,
    'invite-sent-at': inviteSentAt,
    'name-first': nameFirst,
    'name-last': nameLast,
    'profile-picture-url': profilePictureUrl,
    status,
  } = user.attributes as UserResponseAttributes;

  const canEditUser = hasUserWritePermission && !isCurrentUser;
  // When an org uses SAML, we cannot CRUD users.
  const canEditUserAuthentication = canEditUser;
  const canEditUserAvailability = hasUserAvailabilityWritePermission || isCurrentUser;

  const existingRoleIds = user.relationships.roles.data.map((r) => r.id);

  // Not all roles will be visible because of permissions.
  const existingRoleNames = existingRoleIds.map(
    (id) => roles.find((r) => r.id === id)?.attributes?.name,
  );

  const teamNames = compact(
    user.relationships.teams?.data?.map(
      (team) => teams.find((t) => t.id === team.id)?.attributes?.name,
    ) ?? [],
  );

  const availabilityOptions = Object.values(UserAvailability).map((availability) => ({
    label: capitalize(availability),
    value: availability,
  }));

  const dropdownOptions = [
    {
      id: 'edit-roles',
      label: 'Assign roles',
      onClick: showUpdateRolesModal,
      hidden: !(canEditUser && status !== UserStatus.Deactivated),
    },
    {
      id: 'issue-recovery-code',
      label: 'Issue recovery code',
      onClick: () => rotateRecoveryCode(user.id),
      hidden: !(canEditUserAuthentication && status === UserStatus.Active),
    },
    {
      id: 'delete',
      label: 'Deactivate',
      onClick: () => removeUser(user),
      hidden: !(canEditUserAuthentication && status !== UserStatus.Deactivated),
      variant: 'danger' as const,
    },
    {
      id: 'reactivate',
      label: 'Reactivate',
      onClick: () => reactivateUser(user),
      hidden: !(canEditUserAuthentication && status === UserStatus.Deactivated),
    },
  ].filter(({ hidden }) => !hidden);

  return (
    <TableRow data-test={`users-list__user-row--${emailAddress}`}>
      <TableCell data-test={`users-list__user-row--name-display-${emailAddress}`}>
        <TableCellNameWrapper>
          {status === UserStatus.Pending ? (
            <Sticker>Pending</Sticker>
          ) : status === UserStatus.Deactivated ? (
            <Sticker variant='danger'>Deactivated</Sticker>
          ) : profilePictureUrl != null ? (
            <>
              <StyledProfilePictureImage
                height={PROFILE_PICTURE_WIDTH}
                width={PROFILE_PICTURE_WIDTH}
                src={profilePictureUrl}
              />
              <StyledNameParagraph color='primary'>
                {[nameFirst, nameLast].filter(Boolean).join(' ')}
              </StyledNameParagraph>
            </>
          ) : (
            <>
              <StyledIconUserProfilePicture
                height={PROFILE_PICTURE_WIDTH}
                width={PROFILE_PICTURE_WIDTH}
              />
              <StyledNameParagraph color='primary'>
                {[nameFirst, nameLast].filter(Boolean).join(' ')}
              </StyledNameParagraph>
            </>
          )}
        </TableCellNameWrapper>
      </TableCell>
      <Permissioned permission={Permission.TeamView}>
        <StickersCell data-test={`users-list__user-row--team-display-${emailAddress}`}>
          <StickersCellWrapper key={`users-list__user-row--team-display-${emailAddress}`}>
            {teamNames.sort().map((name, i) => (
              <Sticker
                id={`users-list__user-team--${emailAddress}-${name}-${i}`}
                key={`users-list__user-team--${emailAddress}-${name}-${i}`}
                data-test={`users-list__user-team--${emailAddress}-${name}-${i}`}
                variant='primary'
              >
                {name}
              </Sticker>
            ))}
          </StickersCellWrapper>
        </StickersCell>
      </Permissioned>
      <EmailAddressCell
        isActive={status === UserStatus.Active}
        data-test={`users-list__user-row--email-display-${emailAddress}`}
      >
        {isCurrentUser ? (
          `${emailAddress} (You)`
        ) : (
          <>
            <span>{emailAddress}</span>{' '}
            {status === UserStatus.Pending && (
              <Permissioned permission={Permission.UserWrite}>
                <a
                  id={`users-list__reinvite-user--${emailAddress}`}
                  data-test={`users-list__reinvite-user--${emailAddress}`}
                  onClick={showConfirmReinviteModal}
                >
                  (Resend invite)
                </a>
              </Permissioned>
            )}
          </>
        )}
      </EmailAddressCell>
      <StickersCell data-test={`users-list__user-row--role-display-${emailAddress}`}>
        <StickersCellWrapper key={`users-list__user-row--role-display-${emailAddress}`}>
          {existingRoleNames.sort().map((name, i) => {
            return name != null ? (
              <>
                <Sticker
                  id={`users-list__user-role--${emailAddress}-${name}-${i}`}
                  data-test={`users-list__user-role--${emailAddress}-${name}-${i}`}
                  variant='primary'
                >
                  {name}
                </Sticker>{' '}
              </>
            ) : (
              <>
                <Sticker
                  id={`users-list__user-role--${emailAddress}-Restricted-${i}`}
                  data-test={`users-list__user-role--${emailAddress}-Restricted-${i}`}
                >
                  Restricted
                </Sticker>{' '}
              </>
            );
          })}
        </StickersCellWrapper>
      </StickersCell>
      <Permissioned permission={Permission.UserMfaAllUsersView}>
        {availableMfaMethods && (
          <StyledTableCell data-test={`users-list__user-row--mfa-display-${emailAddress}`}>
            {status !== UserStatus.Active ? null : availableMfaMethods.length > 0 ? (
              <Sticker variant='success'>Configured</Sticker>
            ) : (
              <Sticker variant='danger'>Not configured</Sticker>
            )}
          </StyledTableCell>
        )}
      </Permissioned>
      <AvailabilityCell data-test={`users-list__user-row--availability-${emailAddress}`}>
        <ReactSelect
          value={isCurrentUser ? currentUser.attributes.availability : user.attributes.availability}
          components={{
            SingleValue: UserListRowAvailabilitySingleValue,
            Option: UserListRowAvailabilityOption,
          }}
          styles={customStyles}
          menuPosition='fixed'
          options={availabilityOptions}
          filterOption={(option) =>
            isCurrentUser
              ? option.value !== currentUser.attributes.availability
              : option.value !== user.attributes.availability
          }
          disabled={!canEditUserAvailability}
          onChange={handleUpdateAvailability}
          id={`users-list__user-row--availability-${emailAddress}`}
          data-test={`users-list__user-row--availability-${emailAddress}`}
          isSearchable={false}
        />
      </AvailabilityCell>
      <StyledTableCell>
        {dropdownOptions.length > 0 && (
          <ContextMenu
            data-test={`users-list__user-more--${emailAddress}`}
            items={dropdownOptions}
            renderItem={(item) => (
              <ContextMenu.Item
                key={item.id}
                id={`users-list__user-${item.id}--${emailAddress}`}
                data-test={`users-list__user-${item.id}--${emailAddress}`}
                onClick={(e) => {
                  // Needed to avoid triggering row click below
                  e.stopPropagation();
                  e.preventDefault();
                  /* @ts-expect-error [strict-migration] The following line is not strict; please fix! */
                  item.onClick();
                }}
                variant={item.variant}
              >
                {item.label}
              </ContextMenu.Item>
            )}
          />
        )}
      </StyledTableCell>
      <ModalWrapper
        isOpen={shouldShowConfirmReinviteModal}
        handleCloseModal={hideConfirmReinviteModal}
      >
        <ConfirmationModal
          cancelAction={hideConfirmReinviteModal}
          /* @ts-expect-error [strict-migration] The following line is not strict; please fix! */
          confirmAction={() => reinviteUser(user)}
          header='Are you sure?'
          confirmActionText='Send'
          confirmButtonType='primary'
          cancelActionText='Cancel'
        >
          {!inviteExpired && inviteSentAt ? (
            <Paragraph>
              Resending an invite will invalidate a previous invite sent at{' '}
              {DateTime.fromISO(inviteSentAt).toFormat(DATE_AND_TIME_TZ_FORMAT)}.
            </Paragraph>
          ) : (
            <Paragraph>
              Send <b>{emailAddress}</b> a new activation email?
            </Paragraph>
          )}
        </ConfirmationModal>
      </ModalWrapper>
      <ModalWrapper isOpen={updateRolesModal} handleCloseModal={hideUpdateRolesModal}>
        <ModalContainer data-test='edit-user-roles'>
          <ModalHeader data-test='edit-user-roles-modal__header' onClose={hideUpdateRolesModal}>
            Assign roles
          </ModalHeader>

          <ModalBody>
            <Paragraph>
              Select roles for <b>{emailAddress}</b>.
            </Paragraph>
            <FormUpdateRoles
              initialValues={{ roleIds: existingRoleIds }}
              onSubmit={handleUpdateRoles}
              allRoles={roles}
            />
          </ModalBody>
        </ModalContainer>
      </ModalWrapper>
    </TableRow>
  );
};

export default UsersListRow;

const StyledProfilePictureImage = styled('img')`
  border-radius: ${BORDER_RADIUSES.rounded};
  object-fit: cover;
`;

const StyledIconUserProfilePicture = styled(IconUserProfilePicture)`
  border-radius: ${BORDER_RADIUSES.rounded};
`;

const StyledTableCell = styled(TableCell)`
  padding: 0 12px 0 12px;
`;

const TableCellNameWrapper = styled.div`
  display: flex;
  grid-gap: ${Spacing[2]};
  align-items: center;
`;

const EmailAddressCell = styled(TableCell)<{ isActive: boolean }>`
  text-overflow: ellipsis;
  overflow: hidden;
  padding: 0 12px 0 12px;

  ${({ theme, isActive }) =>
    !isActive &&
    css`
      color: ${theme.color.text.disabled};
    `}
`;

const StickersCell = styled(TableCell)`
  max-width: 35%;
`;

const AvailabilityCell = styled(TableCell)`
  min-width: 150px;
`;

const StyledNameParagraph = styled(Paragraph)`
  margin-top: 1px;
`;

const StickersCellWrapper = styled.div`
  display: inline-flex;
  flex-wrap: wrap;
  grid-gap: ${Spacing[1]};
`;
